import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const HalqaStartButton = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Please enter a room title.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post('/api/halqa', { title: title.trim() });
      setShowForm(false);
      setTitle('');
      navigate(`/halqa/${data.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create room. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setTitle('');
    setError('');
  };

  return (
    <>
      {/* Trigger Button */}
      {!showForm && (
        collapsed ? (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            title={t('halqa.startHalqa', 'Start a Halqa')}
            className="w-11 h-11 mx-auto rounded-full border border-dashed border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-rose-400 dark:hover:border-rose-500 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50/30 dark:hover:bg-rose-950/20 transition-all text-base flex items-center justify-center mt-1 shrink-0"
            aria-label={t('halqa.startHalqa', 'Start a Halqa')}
          >
            <span>🎙️</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 w-full px-3.5 py-2.5 rounded-full border border-dashed border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-rose-400 dark:hover:border-rose-500 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50/30 dark:hover:bg-rose-950/20 transition-all text-sm font-medium mt-1"
          >
            <span className="text-base">🎙️</span>
            <span>{t('halqa.startHalqa', 'Start a Halqa')}</span>
          </button>
        )
      )}

      {/* Create Form: Modal when collapsed, inline when expanded */}
      {showForm && (
        collapsed ? (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
            <form
              onSubmit={handleSubmit}
              className="w-full max-w-sm p-4 rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-2xl flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-1.5">
                  <span>🎙️</span> {t('halqa.startHalqa', 'Start a Live Halqa')}
                </p>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 p-1"
                >
                  <i className="fas fa-times text-xs" />
                </button>
              </div>

              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('halqa.titlePlaceholder', "What's this Halqa about?")}
                maxLength={100}
                autoFocus
                className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-rose-400 dark:focus:ring-rose-500 transition"
              />

              {error && (
                <p className="text-xs text-rose-500 font-medium">{error}</p>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 py-2 rounded-full border border-zinc-200 dark:border-zinc-700 text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2 rounded-full bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white text-xs font-bold transition shadow-xs"
                >
                  {loading ? t('common.loading') : `🔴 ${t('halqa.goLiveNow', 'Go Live')}`}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-1 p-3 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900 flex flex-col gap-2.5"
          >
            <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-1.5">
              <span>🎙️</span> {t('halqa.startHalqa', 'Start a Live Halqa')}
            </p>

            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('halqa.titlePlaceholder', "What's this Halqa about?")}
              maxLength={100}
              autoFocus
              className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-rose-400 dark:focus:ring-rose-500 transition"
            />

            {error && (
              <p className="text-xs text-rose-500 font-medium">{error}</p>
            )}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 rounded-full bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white text-sm font-bold transition-colors"
              >
                {loading ? t('common.loading') : `🔴 ${t('halqa.goLiveNow', 'Go Live')}`}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 rounded-full border border-zinc-200 dark:border-zinc-700 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </form>
        )
      )}
    </>
  );
};

export default HalqaStartButton;
